import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnInit,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { NewsletterFormComponent } from 'src/app/shared/forms/newsletter-form/newsletter-form.component';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { SiteService } from 'src/app/core/services/site.service';
import { NewsletterService } from 'src/app/core/services/newsletter.service';
import { AlertService } from 'src/app/core/services/alert.service';
import { Site } from '@core/interfaces/api';

@Component({
  selector: 'app-add-newsletter',
  templateUrl: './add-newsletter.component.html',
  styleUrls: ['./add-newsletter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddNewsletterComponent implements OnInit {
  public selectAllKey = 'all'; // for multi property selector "Select All"
  public newsletterForm = NewsletterFormComponent.formModel();
  public submitting = false;
  public sites: number[] = []; // site id array

  constructor(
    private alertService: AlertService,
    private dialogRef: MatDialogRef<NewsletterFormComponent>,
    private newsletterService: NewsletterService,
    public siteService: SiteService,
    @Inject(MAT_DIALOG_DATA) public batchCreate: boolean = false
  ) {}

  ngOnInit() {}

  public async propertySelectChange(updatedVal: any[]) {
    const selectAllSelected =
      updatedVal.findIndex((val) => val === this.selectAllKey) !== -1;
    if (selectAllSelected) {
      this.sites = await this.siteService.siteIds;
    }
  }

  public async onSave(): Promise<void> {
    this.submitting = true;
    this.batchCreate ? this.saveBatch() : this.saveSingle();
  }

  private async saveSingle() {
    try {
      const siteId = this.siteService.currentSiteId;
      let site = this.siteService.currentSite;

      // If currentSite is not available, fetch it
      if (!site) {
        site = await firstValueFrom(this.siteService.getSite(siteId));
      }

      if (!site) {
        throw new Error(`Unable to resolve site for id ${siteId}`);
      }

      const newsletter = NewsletterFormComponent.serialize(
        this.newsletterForm.value,
        { siteId }
      );

      const savedNewsletter = await this.newsletterService.saveNewsletter(newsletter, null, site);
      await this.newsletterService.refreshNewsletters();
      this.dialogRef.close({
        success: true,
        newsletterId: savedNewsletter.id,
      });
    } catch (error) {
      this.alertService.error('Unable to save Newsletter. Please try again.');
      console.error(error);
    } finally {
      this.submitting = false;
    }
  }

  private async saveBatch() {
    if (!this.sites?.length) {
      return;
    }

    try {
      const allSites = (await firstValueFrom(this.siteService.sites)) ?? [];

      for (const siteId of this.sites) {
        const newsletter = NewsletterFormComponent.serialize(
          this.newsletterForm.value,
          { siteId }
        );
        let site: Site | undefined = allSites.find((s) => s.id === siteId);

        if (!site) {
          site = await firstValueFrom(this.siteService.getSite(siteId));
        }

        if (!site) {
          throw new Error(`Unable to resolve site for id ${siteId}`);
        }

        await this.newsletterService.saveNewsletter(newsletter, null, site);
      }

      await this.newsletterService.refreshNewsletters();
      this.submitting = false;
      this.dialogRef.close({ success: true });
    } catch (error) {
      this.submitting = false;
      this.alertService.error(
        'Error creating batch of Newsletters. Please try again.'
      );
      console.error(error);
    }
  }
}
